import React, {
  useCallback, useContext, useEffect, useState,
} from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import {
  Button, TextField, MenuItem, Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import { Refresh } from '@mui/icons-material';
import UnfoldMore from '@mui/icons-material/UnfoldMore';
import moment from 'moment-timezone';
import { sendRequest } from '../../api/shootAPI';
import API from '../../api/endpoints';
import { UserContext } from '../../contexts/User';
import { NotificationContext } from '../../contexts/Notification';
import './activityLogs.css';
import NavigationLayout from '../../components/NavigationLayout';
import Pagination from '../../components/pagination';
import useDebounce from '../../hooks/useDebounce';
import CustomTable from '../../components/table/CustomTable';
import DateRangePicker from '../../components/dateRangePicker';
import TimezoneSelectWrapper from '../../components/selectors/TimezoneSelectWrapper';

const TABLE_HEADERS = [
  {
    label: 'Date & Time',
    sortable: true,
    sortIcon: UnfoldMore,
    sortKey: 'date',
    colSpan: 1.1,
  },
  {
    label: 'User',
    sortable: true,
    sortIcon: UnfoldMore,
    sortKey: 'user',
    colSpan: 1.1,
  },
  {
    label: 'Role',
    sortable: true,
    sortIcon: UnfoldMore,
    sortKey: 'role',
    colSpan: 0.9,
  },
  {
    label: 'Event',
    sortable: true,
    sortIcon: UnfoldMore,
    sortKey: 'event',
    colSpan: 1.2,
  },
  {
    label: 'Event Type',
    sortable: true,
    sortIcon: UnfoldMore,
    sortKey: 'eventType',
    colSpan: 0.8,
  },
  {
    label: 'Module',
    sortable: true,
    sortIcon: UnfoldMore,
    sortKey: 'module',
    colSpan: 0.8,
  },
  {
    label: 'Description',
    sortable: false,
    colSpan: 1.5,
  },
  {
    label: 'Details',
    sortable: false,
    colSpan: 0.6,
  },
];

const EVENT_TYPE_OPTIONS = [
  { value: '', label: 'All Event Types' },
  { value: 'signin', label: 'Sign In' },
  { value: 'signout', label: 'Sign Out' },
  { value: 'view', label: 'View' },
  { value: 'export', label: 'Export' },
  { value: 'filter', label: 'Filter' },
  { value: 'post', label: 'Create' },
  { value: 'put', label: 'Update' },
  { value: 'delete', label: 'Delete' },
];

const MODULE_OPTIONS = [
  { value: '', label: 'All Modules' },
  { value: 'Authentication', label: 'Authentication' },
  { value: 'Timesheets', label: 'Timesheets' },
  { value: 'Screenshots', label: 'Screenshots' },
  { value: 'API', label: 'API' },
];

function ActivityLogs() {
  const history = useHistory();
  const { search } = useLocation();
  const query = new URLSearchParams(search);
  const page = query.get('page');

  const { activeWorkspace } = useContext(UserContext);
  const { displayNotification } = useContext(NotificationContext);

  const [activityLogs, setActivityLogs] = useState([]);
  const [currentPage, setCurrentPage] = useState(page ? parseInt(page, 10) : 1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCounts, setTotalCounts] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [eventTypeFilter, setEventTypeFilter] = useState('');
  const [moduleFilter, setModuleFilter] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { debounce } = useDebounce();
  const [sort, setSort] = useState(false);
  const [sortKey, setSortKey] = useState('date');
  const [sortOrder, setSortOrder] = useState('desc');
  const [selectedTimezone, setSelectedTimezone] = useState(activeWorkspace?.timezone || 'UTC');
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedDetails, setSelectedDetails] = useState({});
  const [dateRange, setDateRange] = useState(() => {
    const today = moment.tz(selectedTimezone).toDate();
    const weekAgo = moment.tz(selectedTimezone).subtract(7, 'days').toDate();
    return [weekAgo, today];
  });

  const handleViewDetails = (details) => {
    setSelectedDetails(details);
    setShowDetailsModal(true);
  };

  const fetchActivityLogs = useCallback(async () => {
    if (isLoading) return;

    setIsLoading(true);
    const [startDate, endDate] = dateRange;

    const startOfDay = moment.tz(startDate, selectedTimezone)
      .startOf('day')
      .clone()
      .utc()
      .format('YYYY-MM-DD HH:mm:ss');

    const endOfDay = moment.tz(endDate, selectedTimezone)
      .endOf('day')
      .clone()
      .utc()
      .format('YYYY-MM-DD HH:mm:ss');

    try {
      const { success, data } = await sendRequest({
        method: 'get',
        url: API.MEMBER.activityLogs(),
        params: {
          page: currentPage,
          pageSize,
          search: searchQuery,
          eventType: eventTypeFilter,
          module: moduleFilter,
          startDate: startOfDay,
          endDate: endOfDay,
          sortKey,
          sortOrder,
        },
        timeout: 30000,
      });

      if (success) {
        // Utility to ensure safe rendering in table
        // eslint-disable-next-line no-inner-declarations
        function safeCell(value) {
          if (React.isValidElement(value)) return value;
          if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') return value;
          if (value === null || value === undefined) return '-';
          try {
            return JSON.stringify(value);
          } catch (e) {
            return '-';
          }
        }
        const mappedLogs = data.activityLogs.map((log) => {
          // Get user email from member relationship or fallback to legacy userEmail
          const userEmail = log.member?.user?.email || log.userEmail || 'Unknown User';
          // Get role from member's accountType
          const userRole = log.member?.accountType || 'Unknown Role';

          // Parse additional details from newValue with error handling
          let details = {};
          try {
            if (log.newValue) {
              // If newValue is a string, parse it; if it's already an object, use it
              details = typeof log.newValue === 'string' ? JSON.parse(log.newValue) : log.newValue;
            }
          } catch (error) {
            console.warn('Failed to parse newValue JSON:', error);
            details = {};
          }
          let enhancedDescription = log.description || '-';

          // Add specific details based on event type
          if (log.eventType === 'view' && log.module === 'Screenshots' && details.targetMemberName) {
            enhancedDescription = `Viewed screenshot of ${details.targetMemberName}`;
            if (details.screenshotUrl) {
              const fileName = details.screenshotUrl.split('/').pop();
              enhancedDescription += ` (${fileName})`;
            }
          } else if (log.eventType === 'view' && log.module === 'Activity' && details.targetMemberName) {
            enhancedDescription = `Viewed activity details of ${details.targetMemberName}`;
            if (details.dateRange && details.dateRange.start && details.dateRange.end) {
              try {
                const startDate = moment(details.dateRange.start).format('MMM DD');
                const endDate = moment(details.dateRange.end).format('MMM DD');
                enhancedDescription += ` (${startDate} - ${endDate})`;
              } catch (error) {
                console.warn('Error formatting dateRange:', error);
              }
            }
          } else if (log.eventType === 'view' && log.module === 'Timesheets') {
            if (details.dateRange && details.dateRange.start && details.dateRange.end) {
              try {
                const startDate = moment(details.dateRange.start).format('MMM DD');
                const endDate = moment(details.dateRange.end).format('MMM DD');
                enhancedDescription = `Viewed timesheets (${startDate} - ${endDate})`;
              } catch (error) {
                console.warn('Error formatting dateRange:', error);
                enhancedDescription = 'Viewed timesheets';
              }
            } else {
              enhancedDescription = 'Viewed timesheets';
            }
          } else if (log.eventType === 'export' && log.module === 'Timesheets') {
            if (details.memberCount) {
              enhancedDescription = `Exported timesheets data (${details.memberCount} members)`;
            }
          }

          // Ensure enhancedDescription is always a string and not an object
          if (typeof enhancedDescription !== 'string') {
            try {
              enhancedDescription = JSON.stringify(enhancedDescription);
            } catch (e) {
              enhancedDescription = '-';
            }
          }

          // Create role badge component
          const roleColors = {
            'Primary owner': { bg: '#e3f2fd', color: '#1565c0' },
            'Workspace admin': { bg: '#e3f2fd', color: '#1565c0' },
            'Team Leader': { bg: '#f3e5f5', color: '#7b1fa2' },
            'Team member': { bg: '#fff3e0', color: '#ef6c00' },
          };
          const roleStyle = roleColors[userRole] || { bg: '#fce4ec', color: '#c2185b' };

          const roleBadge = (
            <span
              style={{
                fontSize: '12px',
                background: roleStyle.bg,
                color: roleStyle.color,
                padding: '2px 8px',
                borderRadius: '4px',
                fontWeight: '500',
                display: 'inline-block',
                textAlign: 'center',
                minWidth: '80px',
              }}
            >
              {userRole}
            </span>
          );

          // Ensure details is not an object directly rendered as a child
          let detailsCell = '-';
          if (Object.keys(details).length > 0) {
            detailsCell = (
              <Button
                size="small"
                variant="outlined"
                onClick={() => handleViewDetails(details)}
              >
                View
              </Button>
            );
          }

          return {
            dateTime: safeCell(moment(log.date).format('MMM DD, YYYY HH:mm:ss')),
            user: safeCell(userEmail),
            role: roleBadge,
            event: safeCell(log.event),
            eventType: safeCell(log.eventType || '-'),
            module: safeCell(log.module),
            description: safeCell(enhancedDescription),
            details: detailsCell,
            // detailsData: 'details',
            hasDetails: Object.keys(details).length > 0,
          };
        });

        setActivityLogs(mappedLogs);
        setTotalPages(data.totalPages || 1);
        setTotalCounts(data.totalCount || mappedLogs.length);
      } else {
        displayNotification({
          message: 'Failed to fetch activity logs. Please try again.',
          severity: 'error',
        });
      }
    } catch (error) {
      displayNotification({
        message: 'An error occurred while fetching activity logs.',
        severity: 'error',
      });
    } finally {
      setIsLoading(false);
    }
  }, [
    currentPage,
    pageSize,
    searchQuery,
    eventTypeFilter,
    moduleFilter,
    dateRange,
    selectedTimezone,
    sortKey,
    sortOrder,
    displayNotification,
    isLoading,
  ]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    history.push(`/activity-logs?page=${page}`);
  };

  const handlePageSizeChange = (size) => {
    setPageSize(size);
    setCurrentPage(1);
    history.push('/activity-logs?page=1');
  };

  const handleSort = useCallback((key) => {
    const newSortOrder = sortKey === key && sortOrder === 'asc' ? 'desc' : 'asc';
    setSortKey(key);
    setSortOrder(newSortOrder);
    setSort(!sort);
  }, [sortKey, sortOrder, sort]);

  const handleRefresh = () => {
    fetchActivityLogs();
  };

  const handleSelectTimezone = (timezone) => {
    setSelectedTimezone(timezone);
    const newDateRange = [
      moment.tz(timezone).subtract(7, 'days').toDate(),
      moment.tz(timezone).toDate(),
    ];
    setDateRange(newDateRange);
  };

  const handleCloseDetailsModal = () => {
    setShowDetailsModal(false);
    setSelectedDetails({});
  };

  const handleSearchDebounced = debounce((value) => {
    setSearchQuery(value);
    setCurrentPage(1);
  }, 300);

  useEffect(() => {
    // Sync currentPage with URL query param
    const query = new URLSearchParams(window.location.search);
    const pageParam = query.get('page');
    const pageNum = pageParam ? parseInt(pageParam, 10) : 1;
    if (pageNum !== currentPage) {
      setCurrentPage(pageNum);
    }
  }, [window.location.search]);

  useEffect(() => {
    fetchActivityLogs();
  }, [
    currentPage,
    pageSize,
    searchQuery,
    eventTypeFilter,
    moduleFilter,
    dateRange,
    selectedTimezone,
    sortKey,
    sortOrder,
  ]);

  return (
    <NavigationLayout>
      <div className="activity-logs-container">
        <div className="activity-logs-header">
          <div className="title-section">
            <h1>Activity Logs</h1>
            <p className="subtitle">Monitor user activities and system events</p>
          </div>

          <div className="action-buttons">
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={handleRefresh}
              disabled={isLoading}
            >
              Refresh
            </Button>
          </div>
        </div>

        <div className="filters-section">
          <div className="filters-row">
            <TextField
              label="Search users, events, or descriptions"
              variant="outlined"
              size="small"
              className="search-field"
              onChange={(e) => handleSearchDebounced(e.target.value)}
              placeholder="Type to search..."
            />

            <TextField
              select
              label="Event Type"
              variant="outlined"
              size="small"
              value={eventTypeFilter}
              onChange={(e) => {
                setEventTypeFilter(e.target.value);
                setCurrentPage(1);
              }}
              className="filter-field"
            >
              {EVENT_TYPE_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              select
              label="Module"
              variant="outlined"
              size="small"
              value={moduleFilter}
              onChange={(e) => {
                setModuleFilter(e.target.value);
                setCurrentPage(1);
              }}
              className="filter-field"
            >
              {MODULE_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </div>

          <div className="date-range-section">
            <DateRangePicker
              selectedDates={dateRange}
              onDateChange={setDateRange}
              timezone={selectedTimezone}
            />
            <TimezoneSelectWrapper
              value={selectedTimezone}
              onChange={(e) => handleSelectTimezone(e.target.value)}
            />
          </div>
        </div>

        <div className="table-section">
          <CustomTable
            headers={TABLE_HEADERS}
            items={activityLogs}
            loading={isLoading}
            onSort={handleSort}
            sortKey={sortKey}
            sortOrder={sortOrder}
          />

          <div className="pagination-section">
            <Pagination
              currentPage={currentPage}
              pageSize={pageSize}
              totalPages={totalPages}
              totalCounts={totalCounts}
              onPageChange={handlePageChange}
              setPageSize={handlePageSizeChange}
            />
          </div>
        </div>
      </div>

      {/* Details Modal */}
      <Dialog
        open={showDetailsModal}
        onClose={handleCloseDetailsModal}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Activity Details</DialogTitle>
        <DialogContent>
          <pre style={{
            background: '#f5f5f5',
            padding: '16px',
            borderRadius: '4px',
            overflow: 'auto',
            fontSize: '14px',
            lineHeight: '1.4',
          }}
          >
            {selectedDetails && typeof selectedDetails === 'object'
              ? JSON.stringify(selectedDetails, null, 2)
              : 'No details available'}
          </pre>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDetailsModal}>Close</Button>
        </DialogActions>
      </Dialog>
    </NavigationLayout>
  );
}

export default ActivityLogs;
